import type { ErrorEvent } from '@sentry/react';

const isAbortValue = (type?: string, value?: string): boolean =>
  type === 'AbortError' || value?.startsWith('AbortError') === true || value === 'The operation was aborted.';

/**
 * Safari's own media controls call `play()` on a `<video controls>` (the reel player of the Promote
 * Studio) and reject it with an AbortError when a pause or a new load cuts the play in. The rejection
 * has no stack and no frame of ours (MUSIC24-WEB-D). An AbortError that our code throws keeps its
 * stack, so it still reaches Sentry.
 */
export const isStacklessAbortError = (event: ErrorEvent): boolean => {
  const values = event.exception?.values ?? [];
  const hasFrames = values.some((value) => (value.stacktrace?.frames?.length ?? 0) > 0);
  return values.length > 0 && !hasFrames && values.some((value) => isAbortValue(value.type, value.value));
};

/**
 * The Facebook iOS in-app browser injects a script that watches login forms and calls its native
 * bridge. Its `dispatchToBridge` frame points at the HTML page, and iOS rejects the `postMessage`
 * with an InvalidAccessError (MUSIC24-WEB-E, MUSIC24-WEB-F). Our code has no function of that name.
 */
export const isFacebookBridgeError = (event: ErrorEvent): boolean =>
  (event.exception?.values ?? []).some(
    (value) =>
      value.type === 'InvalidAccessError' &&
      (value.stacktrace?.frames ?? []).some((frame) => frame.function === 'dispatchToBridge')
  );

export const webBeforeSend = (event: ErrorEvent): ErrorEvent | null =>
  isStacklessAbortError(event) || isFacebookBridgeError(event) ? null : event;
